import { useAPICacheContext } from 'context/APICacheContext/APICacheContext';
import { useGlobal } from 'context/global/GlobalContext';
import { useRecaptchaV3Token } from './useRecaptchaV3';
import { getCustomData } from 'utils/customData';
import getMetaIntegration from 'utils/getMetaIntegration';
import getCookieConsent from 'utils/getCookieConsent';
import {
  getTiktokIntegration,
  getGoogleIntegration,
} from 'utils/adIdentifiers';
import { ReceiptType } from 'types/ReceiptType';
import { useCallback, useState } from 'react';
import { useAPI } from 'utils/api';
import { normalizePhoneNumber } from 'utils/normalizePhoneNumber';

export type RegisterProductResult = {
  registrationId: string | null;
  receiptDataSnapshot: ReceiptType | null;
};

const useRegisterProduct = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const {
    slug,
    token,
    productDetails,
    receiptData,
    getPersonalDetails,
    setReceiptData,
    setRegistrationId,
    newRegistrationId,
    setNewRegistrationId,
  } = useGlobal();

  const { invalidateCache } = useAPICacheContext();
  const { token: recaptchaToken } = useRecaptchaV3Token();
  const [registerProduct] = useAPI(
    {
      method: 'POST',
      endpoint: `products/register/${slug}`,
    },
    token,
    true,
    false,
    recaptchaToken || ''
  );

  // Save receipt data globally and check here
  const registerProductAndFetch = useCallback(
    async (
      warrantyId: string | null = null,
      emailConsent: boolean = false
    ): Promise<RegisterProductResult> => {
      setLoading(true);
      let registerationResponse: any = null;

      const consentsByUser = [];

      // Normalize in place BEFORE the snapshot below: receiptDataSnapshot feeds
      // the registration-submission dataLayer event, so normalizing only at the
      // POST would leave that event carrying the raw masked value. Drop the key
      // entirely when nothing usable is left, so the server sees no phone rather
      // than a blank one it would have to guard against.
      if (receiptData && typeof receiptData.phoneNumber === 'string') {
        const normalizedReceiptPhone = normalizePhoneNumber(
          receiptData.phoneNumber
        );
        if (normalizedReceiptPhone) {
          receiptData.phoneNumber = normalizedReceiptPhone;
        } else {
          delete (receiptData as any).phoneNumber;
        }
      }

      const receiptDataSnapshot: ReceiptType | null = receiptData
        ? ({ ...receiptData } as ReceiptType)
        : null;

      if (receiptData?.smsConsent) {
        consentsByUser.push(receiptData.smsConsent);
        delete (receiptData as any).smsConsent;
      }

      const customDataObject = getCustomData();
      const hasCustomData = Object.keys(customDataObject).length > 0;
      const customData = hasCustomData
        ? Object.entries(customDataObject).map(([key, value]) => ({
            key,
            value,
          }))
        : undefined;

      try {
        registerationResponse = await registerProduct({
          warrantyId,
          emailConsent,
          registrationId: newRegistrationId,
          receiptData: receiptData || null,
          consentsByUser,
          customData,
          metaIntegration: getMetaIntegration(),
          tiktokIntegration: getTiktokIntegration(),
          googleIntegration: getGoogleIntegration(),
          cookieConsent: getCookieConsent(
            productDetails?.brand?.cookieConsentSettings
          ),
        });
        await getPersonalDetails();
        invalidateCache();
        setRegistrationId(registerationResponse.registrationId);
        return {
          registrationId: registerationResponse?.registrationId ?? null,
          receiptDataSnapshot,
        };
      } catch (error) {
        throw error;
      } finally {
        setLoading(false);
        setReceiptData(null);
        setNewRegistrationId(null);
      }
    },
    // productDetails included so the callback never closes over a stale
    // undefined value from before product details loaded (the consent
    // snapshot reads brand.cookieConsentSettings from it).
    [registerProduct, receiptData, newRegistrationId, productDetails]
  );

  return { registerProductAndFetch, loading };
};

export default useRegisterProduct;
